/**
 * The social grid: the lattice on which settlement-scale reasoning happens.
 *
 * Bands and camps do not think in render chunks or region cells; they think
 * in "this square kilometre and the eight around it". A social cell is 1024 m
 * — exactly four 256 m render chunks on a side — so a cell edge is always a
 * chunk edge, and anything that invalidates a cell invalidates whole chunks.
 *
 * Pure coordinate arithmetic: no state, no time, no randomness. Anything
 * keyed by SocialCellKey is stable across sessions by construction.
 */

export const SOCIAL_CELL_SIZE = 1024;   // metres — exactly 4 render chunks

export type SocialCellKey = `${number},${number}`;

/** The canonical map key for a social cell. */
export const socialKey = (sx: number, sz: number): SocialCellKey => `${sx},${sz}`;

/** The social cell containing a world position. */
export const toSocial = (wx: number, wz: number): { sx: number; sz: number } => ({
  sx: Math.floor(wx / SOCIAL_CELL_SIZE),
  sz: Math.floor(wz / SOCIAL_CELL_SIZE),
});

/** The world-metre centre of a social cell. */
export const socialCenter = (sx: number, sz: number): { x: number; z: number } => ({
  x: (sx + 0.5) * SOCIAL_CELL_SIZE,
  z: (sz + 0.5) * SOCIAL_CELL_SIZE,
});

/** The 8-neighbourhood, in fixed scan order so iteration is deterministic. */
export const socialNeighbors = (sx: number, sz: number): Array<{ sx: number; sz: number }> => {
  const out: Array<{ sx: number; sz: number }> = [];
  for (let dz = -1; dz <= 1; dz++) for (let dx = -1; dx <= 1; dx++) {
    if (dx !== 0 || dz !== 0) out.push({ sx: sx + dx, sz: sz + dz });
  }
  return out;
};
